/**
 * ★ Fase 91 (Doc 02) — Attribution & funnel events (first-party, privacy-first).
 *
 * Preserva end-to-end: utm_source / utm_medium / utm_campaign / utm_content / utm_term,
 * click_id (fbclid · gclid · ttclid · msclkid), first_touch, last_touch, landing_path,
 * referrer e session_id. Tutto viene allegato ai payload dei form (`attribution`) così
 * il CRM conosce origine e percorso del lead anche se arriva da un sub-link.
 *
 * Persistenza minima (nessun cookie di terze parti):
 *   • localStorage  `hcf_ft_v1`  → first touch (scritto UNA sola volta)
 *   • sessionStorage `hcf_lt_v1` → last touch (sessione corrente)
 *   • sessionStorage `hcf_sid_v1` → id sessione anonimo (uuid) per rate-limit/idempotenza
 *
 * Eventi client (cta_click, booking_redirect, prequal_completed, faq_open, video_play)
 * sono inviati a POST /api/public/funnel-event SOLO con consenso analytics (CookieBanner).
 * Gli eventi di verità (lead_created, zoom_booked, pass_paid…) sono registrati dal backend.
 */
import { useEffect } from 'react';
import { api, API_BASE } from './api';
import { getConsent } from '../components/CookieBanner';
import { logger } from './logger';

const FT_KEY = 'hcf_ft_v1';
const LT_KEY = 'hcf_lt_v1';
const SID_KEY = 'hcf_sid_v1';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CLICK_KEYS = ['click_id', 'fbclid', 'gclid', 'ttclid', 'msclkid'];

function safeGet(storage, key) {
  try { const raw = storage.getItem(key); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function safeSet(storage, key, value) {
  try { storage.setItem(key, JSON.stringify(value)); } catch { /* storage pieno o bloccato: ignora */ }
}

function uuid() {
  try { if (window.crypto?.randomUUID) return window.crypto.randomUUID(); } catch { /* noop */ }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Id sessione anonimo (per sessione browser). */
export function getSessionId() {
  if (typeof window === 'undefined') return null;
  let sid = null;
  try { sid = window.sessionStorage.getItem(SID_KEY); } catch { /* noop */ }
  if (!sid) {
    sid = uuid();
    try { window.sessionStorage.setItem(SID_KEY, sid); } catch { /* noop */ }
  }
  return sid;
}

/** Parametri di campagna presenti nell'URL corrente. */
export function parseCampaignParams(search = typeof window !== 'undefined' ? window.location.search : '') {
  const out = { utm: {}, click_id: null, click_source: null };
  try {
    const p = new URLSearchParams(search || '');
    UTM_KEYS.forEach((k) => { const v = p.get(k); if (v) out.utm[k] = v.slice(0, 200); });
    for (const k of CLICK_KEYS) {
      const v = p.get(k);
      if (v) { out.click_id = v.slice(0, 200); out.click_source = k; break; }
    }
  } catch { /* noop */ }
  return out;
}

/**
 * Da chiamare al mount di ogni landing pubblica: registra first touch (una volta)
 * e aggiorna il last touch. Idempotente.
 */
export function captureAttribution() {
  if (typeof window === 'undefined') return null;
  const { utm, click_id, click_source } = parseCampaignParams();
  const touch = {
    ts: new Date().toISOString(),
    landing_path: window.location.pathname + (window.location.search || ''),
    referrer: (document.referrer || '').slice(0, 300) || null,
    utm,
    ...(click_id ? { click_id, click_source } : {}),
  };
  const ft = safeGet(window.localStorage, FT_KEY);
  if (!ft) safeSet(window.localStorage, FT_KEY, touch);
  // last touch: aggiorna solo se cambia qualcosa di rilevante (utm/click) o se assente
  const lt = safeGet(window.sessionStorage, LT_KEY);
  const hasCampaign = Object.keys(utm).length > 0 || !!click_id;
  if (!lt || hasCampaign) safeSet(window.sessionStorage, LT_KEY, touch);
  getSessionId();
  return getAttribution();
}

/** Snapshot completo da allegare ai payload dei form. */
export function getAttribution() {
  if (typeof window === 'undefined') return {};
  const ft = safeGet(window.localStorage, FT_KEY);
  const lt = safeGet(window.sessionStorage, LT_KEY);
  const current = parseCampaignParams();
  const utm = { ...(ft?.utm || {}), ...(lt?.utm || {}), ...current.utm };
  const click_id = current.click_id || lt?.click_id || ft?.click_id || null;
  return {
    ...utm,
    ...(click_id ? { click_id } : {}),
    landing_path: ft?.landing_path || window.location.pathname,
    page_path: window.location.pathname,
    referrer: ft?.referrer || (document.referrer || '').slice(0, 300) || null,
    session_id: getSessionId(),
    first_touch: ft || null,
    last_touch: lt || null,
  };
}

/** Aggiunge gli UTM correnti (o quelli salvati) a un link interno, es. verso /f/it. */
export function withUtm(href) {
  try {
    const attr = getAttribution();
    const url = new URL(href, window.location.origin);
    UTM_KEYS.forEach((k) => { if (attr[k] && !url.searchParams.get(k)) url.searchParams.set(k, attr[k]); });
    return url.pathname + url.search + url.hash;
  } catch { return href; }
}

/** Consenso analytics (CookieBanner → gp_consent_v1). */
export function hasAnalyticsConsent() {
  try { const c = getConsent(); return !!(c?.analytics || c?.marketing); } catch { return false; }
}

/**
 * Evento di engagement client-side (fire-and-forget). Inviato SOLO con consenso analytics.
 * @param {string} event   cta_click | booking_redirect | prequal_completed | faq_open | video_play | section_view
 * @param {object} data    { funnel, lead_id, label, target, section, position, value, beacon }
 *   `beacon: true` → usa navigator.sendBeacon (sopravvive a un redirect immediato, es. verso Calendly)
 */
export function trackFunnelEvent(event, data = {}) {
  if (typeof window === 'undefined') return;
  if (!hasAnalyticsConsent()) return;
  try {
    const { funnel, lead_id, beacon, ...meta } = data;
    const payload = {
      event,
      funnel: funnel || null,
      lead_id: lead_id || null,
      client_event_id: uuid(),
      attribution: getAttribution(),
      meta: { ...meta, page_path: window.location.pathname },
    };
    if (beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(`${API_BASE}/public/funnel-event`, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      if (ok) return;
    }
    api.post('/public/funnel-event', payload).catch(() => {});
  } catch (err) {
    logger.debug('trackFunnelEvent failed', err);
  }
}

/**
 * ★ Fase 94 (Doc 02) — Link Calendly con prefill + UTM preservati.
 * Calendly accetta `name` / `email` (prefill del form invitato) e i parametri `utm_*`
 * (riportati nel webhook invitee.created). `utm_content=lead_id` (solo se la campagna non
 * ha già un utm_content: quello della campagna viene SEMPRE preservato) aiuta a riconciliare
 * la prenotazione anche se l'utente cambia email su Calendly.
 * Nessun dato sensibile oltre nome/email (già forniti dall'utente nel form).
 */
export function withCalendlyPrefill(bookingUrl, { name, email, leadId, funnel } = {}) {
  try {
    const url = new URL(bookingUrl);
    if (name) url.searchParams.set('name', String(name).slice(0, 120));
    if (email) url.searchParams.set('email', String(email).slice(0, 160));
    const attr = getAttribution();
    UTM_KEYS.forEach((k) => { if (attr[k] && !url.searchParams.get(k)) url.searchParams.set(k, attr[k]); });
    if (!url.searchParams.get('utm_source')) url.searchParams.set('utm_source', 'hcf_landing');
    if (funnel && !url.searchParams.get('utm_campaign')) url.searchParams.set('utm_campaign', String(funnel));
    if (leadId && !url.searchParams.get('utm_content')) url.searchParams.set('utm_content', `lead_${leadId}`);
    return url.toString();
  } catch { return bookingUrl; }
}

/** Hook React di comodo: cattura attribution al mount. */
export function useCaptureAttribution() {
  useEffect(() => { captureAttribution(); }, []);
}
